import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  FiFileText,
  FiPrinter,
  FiFile,
  FiCheckCircle,
  FiRepeat,
  FiSlash
} from 'react-icons/fi';
import instanciaAxios from '../../miscelaneo/instanciaAxios';
import { useImpresion } from '../../contexts/ContextoImpresion';
import SpinnerCargando from '../util/SpinnerCargando';
import ModalPDFBase64 from '../util/ModalPDFBase64';

function fmt(valor) {
  return (parseFloat(valor) || 0).toLocaleString('es-DO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

function BadgeEstado({ id_estado, id_factura_convertida }) {
  if (id_factura_convertida) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
        <FiRepeat size={12} />
        Convertida
      </span>
    );
  }
  if (id_estado === 2) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
        <FiSlash size={12} />
        Anulada
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
      <FiCheckCircle size={12} />
      Activa
    </span>
  );
}

function InfoCard({ label, value }) {
  return (
    <div className="bg-gray-50 rounded-lg p-3">
      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</span>
      <p className="text-sm font-semibold text-gray-800 mt-0.5 truncate">{value}</p>
    </div>
  );
}

function TotalCard({ label, value, color }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    purple: 'bg-purple-50 text-purple-700 border-purple-200',
    green: 'bg-green-50 text-green-700 border-green-200'
  };
  return (
    <div className={`rounded-lg p-3 border ${colors[color] || colors.blue}`}>
      <span className="text-xs font-medium opacity-75">{label}</span>
      <p className="text-lg font-bold mt-0.5">${value}</p>
    </div>
  );
}

function TabGeneral({ cotizacion }) {
  if (!cotizacion) return null;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <InfoCard label="Cliente" value={cotizacion.nombre_cliente || 'Consumidor Final'} />
        <InfoCard label="RNC" value={cotizacion.rnc_cliente || '—'} />
        <InfoCard
          label="Fecha de Creación"
          value={cotizacion.fecha_hora_creacion ? new Date(cotizacion.fecha_hora_creacion).toLocaleString('es-DO') : '—'}
        />
        {cotizacion.id_factura_convertida && (
          <InfoCard label="Factura Convertida" value={`#${cotizacion.id_factura_convertida}`} />
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <TotalCard label="Subtotal" value={fmt(cotizacion.monto)} color="blue" />
        <TotalCard label="ITBIS" value={fmt(cotizacion.impuesto_total)} color="purple" />
        <TotalCard label="Total" value={fmt(cotizacion.monto_total)} color="green" />
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Detalle de Productos</h3>
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-2 font-medium text-gray-600">Producto</th>
                <th className="text-right px-4 py-2 font-medium text-gray-600">Cant.</th>
                <th className="text-right px-4 py-2 font-medium text-gray-600">Precio</th>
                <th className="text-right px-4 py-2 font-medium text-gray-600">ITBIS</th>
                <th className="text-right px-4 py-2 font-medium text-gray-600">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(cotizacion.detalles_cotizacion || []).map((d, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-gray-800">{d.descripcion_producto}</td>
                  <td className="px-4 py-2 text-right text-gray-700">{d.cantidad}</td>
                  <td className="px-4 py-2 text-right text-gray-700">{fmt(d.precio)}</td>
                  <td className="px-4 py-2 text-right text-gray-700">{fmt(d.impuesto)}</td>
                  <td className="px-4 py-2 text-right font-medium text-gray-900">{fmt(d.monto_total)}</td>
                </tr>
              ))}
              {(!cotizacion.detalles_cotizacion || cotizacion.detalles_cotizacion.length === 0) && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400">Sin productos</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {cotizacion.comentario && (
        <div className="bg-gray-50 p-3 rounded-lg">
          <span className="text-xs font-medium text-gray-500">Comentario:</span>
          <p className="text-sm text-gray-700 mt-1">{cotizacion.comentario}</p>
        </div>
      )}
    </div>
  );
}

export default function ModalDetalleCotizacion({ abierto, onCerrar, idCotizacion }) {
  const { imprimirDocumento } = useImpresion();
  const [cotizacion, setCotizacion] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [tabActivo, setTabActivo] = useState('general');
  const [mostrarPDF, setMostrarPDF] = useState(false);

  const cargarCotizacion = useCallback(async () => {
    if (!idCotizacion) return;
    setCargando(true);
    try {
      const resp = await instanciaAxios.get(`/cotizacion/${idCotizacion}`);
      setCotizacion(resp.data);
    } catch (error) {
      console.error('Error al cargar cotización:', error);
      toast.error('Error al cargar los detalles de la cotización');
    } finally {
      setCargando(false);
    }
  }, [idCotizacion]);

  useEffect(() => {
    if (abierto && idCotizacion) {
      setTabActivo('general');
      setCotizacion(null);
      cargarCotizacion();
    }
  }, [abierto, idCotizacion, cargarCotizacion]);

  if (!abierto) return null;

  const tabs = [
    { id: 'general', label: 'General', icon: FiFileText },
    { id: 'pdf', label: 'Imprimir', icon: FiPrinter },
    { id: 'ver_pdf', label: 'Ver / Descargar PDF', icon: FiFile }
  ];

  const titulo = cargando
    ? 'Cargando...'
    : cotizacion
      ? `Cotización #${cotizacion.id_cotizacion}`
      : 'Detalle de cotización';

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onCerrar} />

      <div className="fixed inset-4 md:inset-8 lg:inset-12 z-50 flex flex-col bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          <div>
            <h2 className="text-lg font-bold text-gray-800">{titulo}</h2>
            {cotizacion && (
              <div className="flex items-center gap-3 mt-1">
                <BadgeEstado id_estado={cotizacion.id_estado} id_factura_convertida={cotizacion.id_factura_convertida} />
              </div>
            )}
          </div>
          <button
            onClick={onCerrar}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            ✕
          </button>
        </div>

        <div className="flex border-b border-gray-200 bg-white px-6">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const active = tabActivo === tab.id;
            return (
              <button
                key={tab.id}
                onClick={async () => {
                  if (tab.id === 'pdf') {
                    try {
                      const metodo = await imprimirDocumento({ tipoDocumento: 'cotizacion', idDocumento: idCotizacion });
                      if (!metodo) setMostrarPDF(true);
                    } catch (error) {
                      console.warn('[ModalDetalleCotizacion] Error en impresión, abriendo fallback PDF:', error?.message);
                      setMostrarPDF(true);
                    }
                  } else if (tab.id === 'ver_pdf') {
                    setMostrarPDF(true);
                  } else {
                    setTabActivo(tab.id);
                  }
                }}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  active
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {cargando ? (
            <div className="flex items-center justify-center py-20">
              <SpinnerCargando />
            </div>
          ) : !cotizacion ? (
            <div className="text-center py-20 text-gray-400">
              <p>No se pudieron cargar los datos de la cotización</p>
            </div>
          ) : (
            tabActivo === 'general' && <TabGeneral cotizacion={cotizacion} />
          )}
        </div>
      </div>

      <ModalPDFBase64
        mostrar={mostrarPDF}
        onClose={() => setMostrarPDF(false)}
        idCotizacion={idCotizacion}
        tituloDocumento="Cotización"
        tipoDocumento="cotizacion"
      />
    </>
  );
}
